<script setup>
import { ref, reactive, watch, computed } from 'vue';
import { Head, router } from '@inertiajs/vue3';
import DefaultLayout from '@/Layouts/DefaultLayout.vue';
import MaterialSelect from './Partials/MaterialSelect.vue';
import SearchResult from '@/Components/SearchResult.vue';
import Checkbox from '@/Components/Checkbox.vue';
import CheckboxLabel from '@/Components/CheckboxLabel.vue';
import FilterBadge from './Partials/FilterBadge.vue';
import axios from 'axios';
import { groupBy, flatMap } from 'lodash';
import LinkGrid from '../Partials/LinkGrid.vue';
import FilterIcon from '@assets/icons/filter.svg';
import ArrowButton from '@/Components/ArrowButton.vue';
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core';

defineOptions({
    layout: DefaultLayout,
});

const props = defineProps({
    productTypes: {
        type: Array,
        required: true,
    },
    materials: {
        type: Array,
        required: true,
    },
    products: {
        type: Object,
        required: true,
    },
    filters: {
        type: Object,
        required: true,
    },
});

const productTypes = computed(() => props.productTypes);
const materials = computed(() => props.materials);
const filters = computed(() => props.filters);
const products = computed(() => props.products);

const currentPage = ref(1);
const isLoading = ref(false);

const hasMore = ref(currentPage.value < products.value.meta.last_page);
const items = ref(products.value.data);

const groupedProducts = computed(() => {
    const grouped = groupBy(
        items.value,
        (product) => product.productType.title,
    );

    return Object.keys(grouped).map((productType) => ({
        title: productType,
        products: grouped[productType],
    }));
});

const getSelectedForMaterial = (material) => {
    const selectedMaterials = [];

    if (filters.value.materialIds?.includes(material.id)) {
        selectedMaterials.push(material);
    }

    const selectedChildren = material.children.filter((child) =>
        filters.value.materialIds?.includes(child.id),
    );

    return [...selectedMaterials, ...selectedChildren];
};

const productFilters = reactive({
    productTypeIds: filters.value.productTypeIds || [],
    materials: materials.value.map((material) => ({
        material,
        selected: getSelectedForMaterial(material),
    })),
});

const filteredMaterialIds = computed(() =>
    flatMap(productFilters.materials, (material) => material.selected).map(
        (material) => material.id,
    ),
);

const resetProductTypes = () => {
    productFilters.productTypeIds = [];
};

const resetMaterials = () => {
    productFilters.materials = materials.value.map((material) => ({
        material,
        selected: [],
    }));
};

const isMobile = computed(() => useBreakpoints(breakpointsTailwind).smaller('md'));

const refreshData = () => {
    router.get(
        route('shop.index'),
        {
            productTypeIds: productFilters.productTypeIds,
            materialIds: filteredMaterialIds.value,
        },
        {
            preserveScroll: ! isMobile.value.value,
            preserveState: true
        },
    );
};

const getProductTypeById = (productTypeId) =>
    productTypes.value.find((productType) => productType.id === productTypeId);

const onChangeProductType = (productType) => {
    const isInProductTypeIds = productFilters.productTypeIds.includes(
        productType.id,
    );

    if (isInProductTypeIds) {
        productFilters.productTypeIds = productFilters.productTypeIds.filter(
            (id) => id !== productType.id,
        );
    } else {
        productFilters.productTypeIds.push(productType.id);
    }
};

const onRemoveProductType = (productTypeId) => {
    productFilters.productTypeIds = productFilters.productTypeIds.filter(
        (id) => id !== productTypeId,
    );
};

const onRemoveMaterial = (materialId) => {
    productFilters.materials = productFilters.materials.map((material) => ({
        ...material,
        selected: material.selected.filter((child) => child.id !== materialId),
    }));
};

const onLoadMore = async () => {
    try {
        isLoading.value = true;
        const newPage = currentPage.value + 1;

        const response = await axios.post(route('shop.load-more-products'), {
            page: newPage,
            productTypeIds: productFilters.productTypeIds,
            materialIds: productFilters.materialIds,
        });

        const newProducts = response.data.products;

        items.value = [...items.value, ...newProducts];

        if (newProducts.length < 20) {
            hasMore.value = false;

            return;
        }

        currentPage.value = newPage;
    } catch (error) {
        console.error(error);
    } finally {
        isLoading.value = false;
    }
};

watch(productFilters, refreshData, { deep: true });
watch(
    products,
    (newProducts) => {
        items.value = newProducts.data;
        currentPage.value = newProducts.meta.current_page;
        hasMore.value = currentPage.value < newProducts.meta.last_page;
    },
    { deep: true },
);
</script>

<template>
    <Head :title="$t('navigation.shop')">
        <meta property="og:title" :content="$t('meta.shop.title')" />
        <meta property="og:image" content="/assets/og_images/social-image.jpg" />
        <meta property="og:description" :content="$t('meta.shop.description')" />
        <meta name="description" :content="$t('meta.shop.description')" />
    </Head>

    <div class="-mt-16 page-container">
        <hr class="mt-4 mb-8" />
        <div class="sticky md:hidden top-16 z-10 mb-24">
            <div class="absolute top-0 -left-4 sm:left-0 right-0 w-screen sm:w-full flex flex-row items-center justify-between bg-primary text-primary-content h-16 px-4 text-lg">
                <button class="w-full flex justify-between items-center" onclick="filterModal.showModal()">
                    <div>{{ $t('shop.filter') }}</div>
                    <div><FilterIcon class="w-12 h-12" /></div>
                </button>
                <dialog id="filterModal" class="modal modal-bottom left-0 right-0 top-0 bottom-0 bg-primary-content">
                    <div class="flex flex-col h-full min-h-0 w-full text-primary">
                        <form method="dialog">
                          <button class="btn btn-sm btn-circle btn-ghost absolute right-4 top-4 leading-[0] text-4xl">✕</button>
                        </form>

                        <div class="flex flex-shrink-0 p-4 text-4xl font-bold">{{ $t('shop.filter') }}</div>
                        <div class="flex-1 overflow-y-auto px-4">

                            <h3 class="heading-6 mb-3">{{ $t('shop.applied_filters') }}</h3>
                            <div class="flex flex-wrap gap-2 mb-8">
                                <FilterBadge
                                    v-for="productTypeId in productFilters.productTypeIds"
                                    :key="productTypeId"
                                    :title="getProductTypeById(productTypeId)?.title"
                                    @remove="onRemoveProductType(productTypeId)"
                                />
                                <FilterBadge
                                    v-for="material in flatMap(
                                        productFilters.materials,
                                        (material) => material.selected,
                                    )"
                                    :key="material.id"
                                    :title="material.title"
                                    @remove="onRemoveMaterial(material.id)"
                                />
                            </div>

                            <div class="mt-4">
                                <h3 class="heading-6 mt-8 mb-3">{{ $t('material.material')}}</h3>
                                <div class="flex flex-row flex-wrap gap-2 mb-8">
                                    <div
                                        class="btn btn-sm"
                                        :class="{
                                            'btn-primary': !filteredMaterialIds.length,
                                        }"
                                        @click="resetMaterials"
                                    >
                                        {{ $t('labels.show_all') }}
                                    </div>
                                    <MaterialSelect
                                        v-for="material in productFilters.materials"
                                        :key="material.material.id"
                                        v-model="material.selected"
                                        :material="material.material"
                                        class="w-full"
                                    />
                                </div>
                            </div>
                            <div>
                                <h3 class="heading-6 mb-3">{{$t('product-type.product_type')}}</h3>
                                <CheckboxLabel class="">
                                    <template #label>
                                        {{ $t('labels.show_all') }}
                                    </template>
                                    <Checkbox
                                        :checked="
                                            productFilters.productTypeIds.length === 0
                                        "
                                        class="font-mono checkbox-primary checkbox-sm"
                                        @change="resetProductTypes"
                                    />
                                </CheckboxLabel>
                                <CheckboxLabel
                                    v-for="productType in productTypes"
                                    :key="productType.id"
                                    class=""
                                >
                                    <template #label>
                                        {{ productType.title }}
                                    </template>
                                    <Checkbox
                                        :checked="
                                            productFilters.productTypeIds.includes(
                                                productType.id,
                                            )
                                        "
                                        :value="productType.id"
                                        class="font-mono checkbox-primary checkbox-sm"
                                        @change="onChangeProductType(productType)"
                                    />
                                </CheckboxLabel>
                            </div>
                        </div>
                        <div class="p-4 text-xl flex flex-shrink-0">
                            <div class="modal-action w-full">
                              <form method="dialog" class="w-full">
                                <ArrowButton class="w-full">
                                    {{ $t('shop.apply_filter') }}
                                </ArrowButton>
                              </form>
                            </div>
                        </div>
                    </div>
                </dialog>

            </div>
        </div>

        <div class="flex flex-col gap-8 lg:flex-row">
            <div class="hidden md:block lg:w-1/4">
                <h2 class="heading-5 !text-black mb-8">
                    {{ $t('product-type.product_type') }}
                </h2>
                <div class="flex flex-wrap">
                    <CheckboxLabel class="lg:w-full [&_:checked~.label-text]:text-primary">
                        <template #label>
                            {{ $t('labels.all') }}
                        </template>
                        <Checkbox
                            :checked="
                                productFilters.productTypeIds.length === 0
                            "
                            class="font-mono checkbox-primary checkbox-sm"
                            @change="resetProductTypes"
                        />
                    </CheckboxLabel>
                    <CheckboxLabel
                        v-for="productType in productTypes"
                        :key="productType.id"
                        class="lg:w-full [&_:checked~.label-text]:text-primary"
                    >
                        <template #label>
                            {{ productType.title }}
                        </template>
                        <Checkbox
                            :checked="
                                productFilters.productTypeIds.includes(
                                    productType.id,
                                )
                            "
                            :value="productType.id"
                            class="font-mono checkbox-primary checkbox-sm"
                            @change="onChangeProductType(productType)"
                        />
                    </CheckboxLabel>
                </div>
            </div>
            <div class="w-full lg:w-3/4">
                <h2 class="hidden md:block heading-5 !text-black mb-8">
                    {{ $t('material.material') }}
                </h2>
                <div class="hidden md:flex flex-row md:flex-wrap gap-4 mb-8">
                    <div
                        class="btn btn-sm"
                        :class="{
                            'btn-primary': !filteredMaterialIds.length,
                        }"
                        @click="resetMaterials"
                    >
                        {{ $t('labels.all') }}
                    </div>
                    <MaterialSelect
                        v-for="material in productFilters.materials"
                        :key="material.material.id"
                        v-model="material.selected"
                        :material="material.material"
                    />
                </div>

                <div class="hidden md:flex divider" />

                <h2 class="my-8 heading-4">{{ $t('shop.search_results') }}</h2>

                <div class="flex flex-wrap gap-2 mb-8">
                    <FilterBadge
                        v-for="productTypeId in productFilters.productTypeIds"
                        :key="productTypeId"
                        :title="getProductTypeById(productTypeId)?.title"
                        @remove="onRemoveProductType(productTypeId)"
                    />
                    <FilterBadge
                        v-for="material in flatMap(
                            productFilters.materials,
                            (material) => material.selected,
                        )"
                        :key="material.id"
                        :title="material.title"
                        @remove="onRemoveMaterial(material.id)"
                    />
                </div>

                <template v-if="groupedProducts?.length">
                    <div class="space-y-8">
                        <div v-for="group in groupedProducts" :key="group.title">
                            <h3 class="mb-8 !text-black heading-5">
                                {{ group.title }}
                            </h3>
                            <div class="space-y-2">
                                <SearchResult
                                    v-for="(product, index) in group.products"
                                    :key="product.id"
                                    :title="product.title"
                                    :material="product.material"
                                    :image="product.materialProductType"
                                    :url="
                                        route('shop.show-product', {
                                            product: product.productUrl,
                                        })
                                    "
                                    :class="{'!mt-6': group.products[index - 1]?.material?.parent.title !== product?.material?.parent.title}"
                                />
                            </div>
                        </div>
                    </div>
                    <div v-if="hasMore" class="flex justify-center mt-16">
                        <div
                            v-if="isLoading"
                            class="loading loading-dots text-primary"
                            aria-hidden
                        ></div>
                        <button
                            v-else
                            type="button"
                            class="btn btn-primary btn-outline btn-sm"
                            @click="onLoadMore"
                        >
                            {{ $t('labels.load_more') }}
                        </button>
                    </div>
                </template>
                <template v-else>
                    <div>
                        {{ $t('shop.no_products_found') }}
                    </div>
                </template>
            </div>
        </div>

        <section class="mt-16">
            <LinkGrid />
        </section>
    </div>
</template>
